'use client';

import { create } from 'zustand';
import type { TaskStatus, WsTask } from '../types';
import {
  moveTask as apiMoveTask,
  toggleSubtask as apiToggleSubtask,
} from '../lib/api';

interface KanbanState {
  columns: Record<TaskStatus, WsTask[]>;
  activeTask: WsTask | null;
  activeLayer: string | null;
  subtasks: Record<string, { text: string; done: boolean }[]>;
  isLoading: boolean;
}

interface KanbanActions {
  setTasks: (tasks: WsTask[]) => void;
  moveTask: (
    taskId: string,
    from: TaskStatus,
    to: TaskStatus,
    position: number
  ) => void;
  setActiveTask: (task: WsTask | null) => void;
  setActiveLayer: (layerId: string | null) => void;
  setSubtasks: (taskId: string, subtasks: { text: string; done: boolean }[]) => void;
  toggleSubtask: (taskId: string, index: number) => void;
  completeTask: (taskId: string, csScore: number) => void;
}

const emptyColumns: Record<TaskStatus, WsTask[]> = {
  todo: [],
  doing: [],
  support: [],
  done: [],
};

export const useKanbanStore = create<KanbanState & KanbanActions>((set) => ({
  columns: { ...emptyColumns },
  activeTask: null,
  activeLayer: null,
  subtasks: {},
  isLoading: false,

  setTasks: (tasks) => {
    const columns: Record<TaskStatus, WsTask[]> = {
      todo: [],
      doing: [],
      support: [],
      done: [],
    };

    for (const task of tasks) {
      if (columns[task.status]) {
        columns[task.status].push(task);
      }
    }

    // Sort each column by position
    for (const status of Object.keys(columns) as TaskStatus[]) {
      columns[status].sort((a, b) => a.position - b.position);
    }

    set({ columns, isLoading: false });
  },

  moveTask: (taskId, from, to, position) => {
    set((state) => {
      const fromTasks = [...state.columns[from]];
      const toTasks = from === to ? fromTasks : [...state.columns[to]];

      const taskIndex = fromTasks.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) return state;

      const [task] = fromTasks.splice(taskIndex, 1);
      const updatedTask: WsTask = {
        ...task,
        status: to,
        position,
        last_moved_at: new Date().toISOString(),
        started_at:
          to === 'doing' && !task.started_at
            ? new Date().toISOString()
            : task.started_at,
        completed_at:
          to === 'done' ? new Date().toISOString() : null,
      };

      toTasks.splice(position, 0, updatedTask);

      // Recalculate positions
      const reindexed = toTasks.map((t, i) => ({ ...t, position: i }));

      // Persistir no Supabase (fire-and-forget, optimistic update)
      apiMoveTask(taskId, to, position).catch((err) => {
        console.warn('Falha ao persistir moveTask:', err);
      });

      return {
        columns: {
          ...state.columns,
          [from]: from === to ? reindexed : fromTasks,
          [to]: reindexed,
        },
      };
    });
  },

  setActiveTask: (task) => set({ activeTask: task }),

  setActiveLayer: (layerId) => set({ activeLayer: layerId }),

  setSubtasks: (taskId, subtasks) => {
    set((state) => ({
      subtasks: { ...state.subtasks, [taskId]: subtasks },
    }));
  },

  toggleSubtask: (taskId, index) => {
    set((state) => {
      const taskSubtasks = state.subtasks[taskId];
      if (!taskSubtasks || index < 0 || index >= taskSubtasks.length) return state;

      const newChecked = !taskSubtasks[index].done;
      const updated = taskSubtasks.map((st, i) =>
        i === index ? { ...st, done: newChecked } : st
      );

      // Persistir no Supabase (fire-and-forget)
      apiToggleSubtask(taskId, index, newChecked).catch((err) => {
        console.warn('Falha ao persistir toggleSubtask:', err);
      });

      return {
        subtasks: { ...state.subtasks, [taskId]: updated },
      };
    });
  },

  completeTask: (taskId, csScore) => {
    set((state) => {
      // Find the task across all columns
      let fromStatus: TaskStatus | null = null;
      let taskIndex = -1;

      for (const status of Object.keys(state.columns) as TaskStatus[]) {
        const idx = state.columns[status].findIndex((t) => t.id === taskId);
        if (idx !== -1) {
          fromStatus = status;
          taskIndex = idx;
          break;
        }
      }

      if (!fromStatus || taskIndex === -1) return state;

      const fromTasks = [...state.columns[fromStatus]];
      const [task] = fromTasks.splice(taskIndex, 1);

      const completedTask: WsTask = {
        ...task,
        status: 'done',
        cs_score: csScore as WsTask['cs_score'],
        completed_at: new Date().toISOString(),
        last_moved_at: new Date().toISOString(),
      };

      const doneTasks = fromStatus === 'done'
        ? fromTasks
        : [...state.columns.done];

      completedTask.position = doneTasks.length;
      doneTasks.push(completedTask);

      const reindexedDone = doneTasks.map((t, i) => ({ ...t, position: i }));

      // Persistir no Supabase (fire-and-forget, optimistic update)
      apiMoveTask(taskId, 'done', doneTasks.length - 1, csScore).catch((err) => {
        console.warn('Falha ao persistir completeTask:', err);
      });

      return {
        columns: {
          ...state.columns,
          [fromStatus]: fromStatus === 'done' ? reindexedDone : fromTasks,
          done: reindexedDone,
        },
        activeTask: null,
      };
    });
  },
}));
